import { useState, useCallback } from 'react';
import { apiClient } from '../../utils/api';
import { AgentHistoryItem, AgentHistoryResponse } from '../../types/user';

interface UseAgentHistoryReturn {
  historyItems: AgentHistoryItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  fetchHistory: () => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const useAgentHistory = (): UseAgentHistoryReturn => {
  const [historyItems, setHistoryItems] = useState<AgentHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchHistory = useCallback(async (page: number = 1, isRefresh: boolean = false) => {
    if (page === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);

    try {
      const response = await apiClient.get<AgentHistoryResponse>(
        `/api/v1/user/agent/history?p=${page}`
      );

      if (response.data.status) {
        const newItems = response.data.data;
        
        if (page === 1 || isRefresh) {
          // 첫 페이지이거나 새로고침인 경우 기존 데이터 교체
          setHistoryItems(newItems);
        } else {
          // 추가 페이지인 경우 기존 데이터에 추가 (중복 제거)
          setHistoryItems(prev => {
            const existingIds = new Set(prev.map(item => item.id));
            const uniqueNewItems = newItems.filter(item => !existingIds.has(item.id));
            return [...prev, ...uniqueNewItems];
          });
        }

        setHasMore(newItems.length >= 10);
        setCurrentPage(page);
      } else {
        setError('히스토리 정보를 가져오는데 실패했습니다.');
      }
    } catch (error) {
      console.error('히스토리 조회 실패:', error);
      setError('히스토리 정보를 가져오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;
    
    await fetchHistory(currentPage + 1, false);
  }, [currentPage, hasMore, isLoadingMore, fetchHistory]);

  const refresh = useCallback(async () => {
    setCurrentPage(1);
    setHasMore(true);
    await fetchHistory(1, true);
  }, [fetchHistory]);

  const initialFetch = useCallback(async () => {
    if (historyItems.length === 0) {
      await fetchHistory(1, false);
    }
  }, [fetchHistory, historyItems.length]);

  return {
    historyItems,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    fetchHistory: initialFetch,
    loadMore,
    refresh,
  };
}; 